'use client';

import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Search } from 'lucide-react';
import { TournamentCardSkeleton } from '@/components/tournament/TournamentCardSkeleton';
import { TournamentFormat, type Tournament } from '@/types/tournament.model';
import { TournamentCard } from './TournamentCard';
import { TrophyIcon } from 'lucide-react';
import { TournamentFilters } from './TournamentFilters';
import {
  getTournamentStatus,
  TournamentStatus,
} from '@/utils/tournament-status';
import { TournamentSortOption, sortTournaments } from '@/utils/tournament-sort';

interface TournamentListProps {
  tournaments: Tournament[];
  isLoading: boolean;
  error: string | null;
  onTournamentClick: (tournamentId: string) => void;
}

export function TournamentList({
  tournaments,
  isLoading,
  error,
  onTournamentClick,
}: TournamentListProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSports, setSelectedSports] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<TournamentStatus[]>([
    TournamentStatus.OPEN,
    TournamentStatus.IN_PROGRESS,
  ]);
  const [selectedFormats, setSelectedFormats] = useState<TournamentFormat[]>(
    [],
  );
  const [sortOption, setSortOption] = useState<TournamentSortOption>(
    TournamentSortOption.START_DATE_ASC,
  );
  const [filteredTournaments, setFilteredTournaments] =
    useState<Tournament[]>(tournaments);

  // Update filtered tournaments when filters or tournaments change
  useEffect(() => {
    let filtered = tournaments;

    // Apply search filter
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((tournament) =>
        tournament.name.toLowerCase().includes(query),
      );
    }

    // Apply sport filter (if any sport is selected)
    if (selectedSports.length > 0) {
      filtered = filtered.filter(
        (tournament) =>
          tournament.sport && selectedSports.includes(tournament.sport.uid),
      );
    }

    // Apply format filter (if any format is selected)
    if (selectedFormats.length > 0) {
      filtered = filtered.filter(
        (tournament) =>
          tournament.format !== undefined &&
          selectedFormats.includes(tournament.format),
      );
    }

    // Apply status filter (if any status is selected)
    if (selectedStatuses.length > 0) {
      filtered = filtered.filter((tournament) => {
        const status = getTournamentStatus(tournament);
        return selectedStatuses.includes(status);
      });
    }

    // Apply sorting
    filtered = sortTournaments(filtered, sortOption);

    setFilteredTournaments(filtered);
  }, [
    searchQuery,
    tournaments,
    selectedSports,
    selectedStatuses,
    selectedFormats,
    sortOption,
  ]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="space-y-3 rounded-lg bg-gray-50 p-3">
          {/* Search skeleton - mirrors the full-width search input row */}
          <div className="h-9 w-full animate-pulse rounded bg-gray-200" />
          {/* Status filters skeleton - mirrors the wrapping status toggle row */}
          <div className="flex w-full flex-wrap gap-2">
            <div className="h-9 w-20 animate-pulse rounded bg-gray-200" />
            <div className="h-9 w-20 animate-pulse rounded bg-gray-200" />
            <div className="h-9 w-24 animate-pulse rounded bg-gray-200" />
            <div className="h-9 w-24 animate-pulse rounded bg-gray-200" />
          </div>
          {/* Sort, sports and format skeleton - mirrors the row that stacks on mobile and wraps from sm up */}
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
            <div className="h-8 w-full animate-pulse rounded bg-gray-200 sm:w-52" />
            <div className="h-8 w-full animate-pulse rounded bg-gray-200 sm:w-40" />
            <div className="h-8 w-full animate-pulse rounded bg-gray-200 sm:w-40" />
          </div>
        </div>
        {Array(5)
          .fill(0)
          .map((_, index) => (
            <TournamentCardSkeleton key={index} />
          ))}
      </div>
    );
  }

  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  if (tournaments.length === 0) {
    return (
      <Card className="flex flex-col items-center justify-center p-12 text-center">
        <div className="mb-6 rounded-full bg-gray-100 p-6">
          <TrophyIcon className="h-16 w-16 text-gray-400" />
        </div>
        <h3 className="mb-3 text-2xl font-semibold text-gray-900">
          Aún no has creado ningún torneo
        </h3>
        <p className="max-w-lg text-gray-600">
          Empieza a organizar tu primer evento.
        </p>
        <p className="max-w-lg text-gray-600">
          Haz clic en el botón para "Crear Nuevo Torneo" los detalles y empezar
          a recibir inscripciones.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters and Search Section */}
      <div className="space-y-3 rounded-lg bg-gray-50 p-3">
        {/* Filters - First and Second Rows */}
        <TournamentFilters
          selectedSports={selectedSports}
          selectedStatuses={selectedStatuses}
          selectedFormats={selectedFormats}
          sortOption={sortOption}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onSportsChange={setSelectedSports}
          onStatusesChange={setSelectedStatuses}
          onFormatsChange={setSelectedFormats}
          onSortChange={setSortOption}
        />
      </div>

      {/* Tournament List */}
      {filteredTournaments.length === 0 ? (
        <Card className="flex flex-col items-center justify-center p-8 text-center">
          <div className="mb-4 rounded-full bg-gray-100 p-4">
            <Search className="h-10 w-10 text-gray-400" />
          </div>
          <h3 className="mb-2 text-xl font-semibold text-gray-900">
            No se encontraron torneos
          </h3>
          <p className="max-w-md text-gray-600">
            No hay torneos que coincidan con los filtros seleccionados.
            {searchQuery && (
              <span className="mt-1 block">
                Búsqueda: "<span className="font-medium">{searchQuery}</span>"
              </span>
            )}
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredTournaments.map((tournament) => (
            <TournamentCard
              key={tournament.id}
              tournament={tournament}
              onClick={() => onTournamentClick(tournament.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
